import React, { useLayoutEffect } from 'react';
import { useNavigation } from 'expo-router';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, DisplayText, TagLabel, useResponsive } from '@garnish/core';
import { ProgressBar } from '@garnish/media';

import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { OrderStatusCtas } from '../OrderStatusCtas';
import { useOrderStatusDetails } from './hooks/useOrderStatusDetails';
import { useOrderStatusTitle } from './hooks/useOrderStatusTitle';
import { useProgressNodes } from './hooks/useProgressNodes';

import type { AddressType } from '@garnish/location';
import type { ProgressBarNodeType } from '@garnish/media';

// ────────────────────────────────────────────────────────────────────────────────

export const OrderStatusInfo = (props: OrderStatusInfoProps) => {
  const {
    orderId = '',
    orderStatus,
    orderType,
    orderTotal = 0,
    vendorId,
    wantedTime,
    estimatedDeliveryTime,
    locationName,
    locationAddress,
    isTrackable,
    canRateOrder,
    isPast,
    canReorder,
    progressStepIcons,
    isGroupOrder,
    onReorder,
    onRateOrder,
  } = props;

  const { minWidth, match, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  // ────────────────────────────────────────────────────────────────

  const shouldShowProgressBar = checkIfShouldShowProgressBar(orderStatus);
  const shouldRenderOrderStatusCTAs = canReorder || canRateOrder;

  const orderStatusTitle = useOrderStatusTitle({
    orderType,
    orderStatus,
    isTrackable,
    isPast,
  });
  const orderStatusDetails = useOrderStatusDetails({
    orderType,
    orderStatus,
    isTrackable,
    isPast,
    wantedTime,
    estimatedDeliveryTime,
    locationName,
    locationAddress,
  });
  const progressNodes = useProgressNodes(orderType, orderStatus);
  const progressNodesWithIcons = getProgressNodesWithIcons({ progressNodes, progressStepIcons });

  // ─── Set screen title ──────────────────────────────────────────

  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({ title: orderStatusTitle });
  }, [navigation, orderStatusTitle]);

  // ─── Styles ────────────────────────────────────────────────────

  const headerStyles = match([styles.infoHeaderXS, styles.infoHeaderSM]);
  const ctasWrapperStyle = match([undefined, styles.ctasWrapperSM]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <View testID="order-status-info">
      <View style={headerStyles}>
        <DisplayText sizeMatch={['32', '40']}>{orderStatusTitle}</DisplayText>

        <View style={styles.orderMetadata}>
          <BodyText sizeMatch={['14']} bold={currentBreakpoint.isXS}>
            {formatMessage(messages.orderNumber, { order_id: vendorId })}
          </BodyText>

          {isGroupOrder ? (
            <View style={styles.groupOrderLabelContainer}>
              <TagLabel sizeMatch={['10']} bold>
                {formatMessage(messages.groupOrder)}
              </TagLabel>
            </View>
          ) : null}
        </View>
      </View>

      {orderStatusDetails ? (
        <BodyText sizeMatch={['14', '16']}>{orderStatusDetails}</BodyText>
      ) : null}

      {shouldShowProgressBar ? (
        <View style={styles.progressBar}>
          <ProgressBar nodes={progressNodesWithIcons} maxFontSizeMultiplier={1.3} />
        </View>
      ) : null}

      {minWidth.isSM && shouldRenderOrderStatusCTAs ? (
        <View style={ctasWrapperStyle}>
          <OrderStatusCtas
            orderId={orderId}
            orderTotal={orderTotal}
            canRateOrder={canRateOrder}
            canReorder={canReorder}
            onReorder={onReorder}
            onRateOrder={onRateOrder}
          />
        </View>
      ) : null}
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  infoHeaderXS: {
    gap: theme.spacing['4'],
  },
  infoHeaderSM: {
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['4'],
    flexDirection: 'column-reverse',
  },
  progressBar: {
    marginTop: theme.spacing['4'],
    paddingTop: theme.spacing['4'],
  },
  ctasWrapperSM: {
    paddingTop: theme.spacing['4'],
  },
  groupOrderLabelContainer: {
    marginBottom: theme.spacing['2'],
  },
  orderMetadata: {
    alignItems: 'flex-start',
    gap: theme.spacing['2'],
  },
});

// ─── Utils ──────────────────────────────────────────────────────────────────────

const checkIfShouldShowProgressBar = (orderStatus?: FlattenedOrderStatuses) =>
  orderStatus !== FlattenedOrderStatuses.Completed && orderStatus !== FlattenedOrderStatuses.Failed;

function getProgressNodesWithIcons(params: GetProgressNodesWithIconsParams) {
  const { progressNodes, progressStepIcons = [] } = params;

  const iconsByStepIndex = new Map(progressStepIcons.map(getStepIndexEntry));

  return progressNodes.map(addStepIcon);

  function addStepIcon(progressNode: ProgressNode, stepIndex: number) {
    return { ...progressNode, icon: iconsByStepIndex.get(stepIndex) };
  }
}

function getStepIndexEntry(progressStepIcon: ProgressStepIcon) {
  const { stepIndex, ...icon } = progressStepIcon;

  return [stepIndex, icon] as const;
}

// ─── Types ──────────────────────────────────────────────────────────────────────

export type OrderStatusInfoProps = {
  orderId?: string;
  orderTotal?: number;
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  vendorId?: string;
  wantedTime?: string;
  estimatedDeliveryTime?: string;
  locationName?: string;
  locationAddress?: string;
  isTrackable: boolean;
  canRateOrder: boolean;
  isPast: boolean;
  momoSurveyUrl?: string | null;
  canReorder: boolean;

  /**
   * Icons of the progress steps. Steps without an icon render the default
   * progress dot.
   */
  progressStepIcons?: ProgressStepIcon[];

  isGroupOrder?: boolean;
  onReorder?: () => void;
  onRateOrder?: () => void;
};

type GetProgressNodesWithIconsParams = {
  progressNodes: ProgressNode[];
  progressStepIcons: ProgressStepIcon[] | undefined;
};

type ProgressNode = ReturnType<typeof useProgressNodes>[number];

type ProgressStepIcon = NonNullable<ProgressBarNodeType['icon']> & { stepIndex: number };

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly groupOrder: NoMessageValues;
  readonly orderNumber: { readonly order_id: MessageValue };
}>({
  groupOrder: {
    defaultMessage: 'Group order',
    description: 'Order Status | Info | Group order label',
  },
  orderNumber: {
    defaultMessage: 'Order #{order_id}',
    description: 'Order Status | Info | Order number display',
  },
});
